import { useState, useEffect, createContext, useContext } from "react";
// ClassroomSidebar REMOVED - Zero-Box Protocol
import { InvisibleGuide, AutoTeacher, VoiceControl } from "./InvisibleGuide";
import { useAudioManager, AudioControlPanel } from "./AudioManager";
import { TextHighlighter, ActiveNarrator } from "./TextHighlighter";
import { VisualContextEngine } from "./VisualContextEngine";
import { SocraticDebateEngine } from "./SocraticDebateEngine";
import { AnimatedAvatar, AvatarWithIndicator } from "./AnimatedAvatar";
import { isYoungLearnerGrade } from "../../utils/gradeUtils";

// THE GUIDE - One unified persona (no separate personas)
const THE_GUIDE = {
  name: "The Guide",
  avatar: "📜",
  color: "#FFD700",
  bgGradient: "linear-gradient(135deg, #1a1a3a 0%, #0a0a2a 100%)",
  style: "invisible, narrates lesson text only, voice-first interaction",
};

// Voice maturity based on grade level (Ascension Path)
const getGuideVoice = (gradeLevel) => {
  if (!gradeLevel) return "echo";
  const level = gradeLevel.toUpperCase();
  if (level.includes("K") || level.includes("1") || level.includes("2")) return "shimmer";
  if (level.includes("3") || level.includes("4") || level.includes("5")) return "nova";
  if (level.includes("6") || level.includes("7") || level.includes("8")) return "echo";
  if (level.includes("9") || level.includes("10") || level.includes("11") || level.includes("12")) return "coral";
  if (level.includes("COLLEGE")) return "onyx";
  if (level.includes("MASTER")) return "sage";
  if (level.includes("ORDINATION")) return "fable";
  return "echo";
};

// Context for classroom intelligence state
const ClassroomContext = createContext(null);

export const useClassroom = () => useContext(ClassroomContext);

// Main Classroom Intelligence Provider
// INVISIBLE GUIDE PROTOCOL: One unified Guide, no separate personas
export function ClassroomIntelligenceProvider({ children, user, gradeLevel }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [showAudioPanel, setShowAudioPanel] = useState(false);
  const [studentProgress, setStudentProgress] = useState({
    currentSection: null,
    completedSections: [],
    lastActivity: null
  });
  
  const audioManager = useAudioManager();
  
  // THE GUIDE: ONE unified persona that evolves based on grade level
  const guideVoice = getGuideVoice(gradeLevel);
  const persona = {
    ...THE_GUIDE,
    voice: guideVoice,
    autoRead: isYoungLearnerGrade(gradeLevel),
  };
  
  // Track when student completes a section
  const markSectionComplete = (sectionId) => {
    setStudentProgress(prev => {
      if (prev.completedSections.includes(sectionId)) return prev;
      
      // Play completion sound
      audioManager.playCompletionSting();
      
      return {
        ...prev,
        completedSections: [...prev.completedSections, sectionId],
        lastActivity: new Date().toISOString()
      };
    });
  };
  
  // Update current lesson context
  const updateLessonContext = (lesson) => {
    setCurrentLesson(lesson);
  };
  
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
    
    // Start ambient music when opening sidebar for first time
    if (!isSidebarOpen && !audioManager.isAmbientPlaying) {
      audioManager.startAmbientMusic();
    }
  };
  
  // Handle instructor speaking state
  const setInstructorSpeaking = (speaking) => {
    audioManager.duckForSpeech(speaking);
  };
  
  const contextValue = {
    isSidebarOpen,
    toggleSidebar,
    currentLesson,
    updateLessonContext,
    persona,
    guideVoice,
    gradeLevel,
    studentProgress,
    markSectionComplete,
    audioManager,
    showAudioPanel,
    setShowAudioPanel,
    setInstructorSpeaking
  };
  
  return (
    <ClassroomContext.Provider value={contextValue}>
      {children}
      
      {/* Note: ClassroomSidebar is now rendered by individual components 
          (LessonWorkspace, UnifiedCourses) to avoid duplication */}
      
      {/* Audio Control Panel */}
      <AudioControlPanel
        isVisible={showAudioPanel}
        onClose={() => setShowAudioPanel(false)}
        audioManager={audioManager}
      />
      
      {/* Audio Settings Button */}
      {isSidebarOpen && (
        <button
          onClick={() => setShowAudioPanel(!showAudioPanel)}
          style={{
            position: "fixed",
            bottom: "100px",
            right: isSidebarOpen ? "395px" : "20px",
            width: "45px",
            height: "45px",
            borderRadius: "50%",
            background: "#1a1a2a",
            border: "2px solid #FFD700",
            color: "#FFD700",
            cursor: "pointer",
            zIndex: 9998,
            transition: "all 0.3s ease-in-out",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.2rem"
          }}
          title="Audio Settings"
        >
          {audioManager.isMuted ? "🔇" : "🎵"}
        </button>
      )}
    </ClassroomContext.Provider>
  );
}

// Export all components - INVISIBLE GUIDE PROTOCOL (Zero-Box)
// ClassroomSidebar REMOVED - No chat bubbles allowed
export { THE_GUIDE, getGuideVoice };
export { useAudioManager, AudioControlPanel } from "./AudioManager";
export { TextHighlighter, ActiveNarrator } from "./TextHighlighter";
export { VisualContextEngine } from "./VisualContextEngine";
export { SocraticDebateEngine } from "./SocraticDebateEngine";
export { AnimatedAvatar, AvatarWithIndicator } from "./AnimatedAvatar";
export { 
  ProactiveInstructorProvider, 
  useProactiveInstructor,
  useDOMContentScraper,
  useExaminationFirewall,
  useWordByWordTTS,
  KaraokeText
} from "./ProactiveInstructor";

// Export InvisibleGuide components
export { InvisibleGuide, AutoTeacher, VoiceControl };

export default ClassroomIntelligenceProvider;
